import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { catchError, EMPTY, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import { BookingDataDetails, CartridgeServicePackage } from '@markant-platform/cartridge/model';
import { TableDraft, TableDraftBuilder } from '@markant-platform/shared/table/model';
import { TABLE_HEADER_AND_CELLS } from './config/usage-report.config';
import { CommonModule } from '@angular/common';
import { MarkantElementsModule } from '@markant/elements-angular';
import { UiCardComponent } from '@markant-platform/shared/ui-card';
import { TranslocoDatePipe, TranslocoDecimalPipe } from '@jsverse/transloco-locale';
import { TranslocoModule } from '@jsverse/transloco';
import { UsageReportFilterComponent } from '../usage-report-filter/usage-report-filter.component';
import {
  UsageAggregationFilter,
  UsageAggregationFrequencyDateFormatMap,
  UsageAggregationPage,
  UsageAggregationService,
} from '@markant-platform/usage/data-access';
import { UsageReportTableComponent } from '../usage-report-table/usage-report-table.component';
import { UsageReportChartComponent } from '../usage-report-chart/usage-report-chart.component';
import { UsageReportViewMode, UsageReportViewModeMap } from './model/usage-report.model';
import { CMSLanguageService } from '@markant-platform/shared/data-access';
import { BookingService } from '@markant-platform/cartridge/data-access';
import { DefaultValuePipe } from '@markant-platform/shared/util-pipes';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';
import { StaticRole } from '@markant-platform/shared/user/model';

@Component({
  selector: 'markant-platform-usage-report',
  templateUrl: './usage-report.component.html',
  styleUrls: ['./usage-report.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MarkantElementsModule,
    UiCardComponent,
    TranslocoDatePipe,
    TranslocoModule,
    UsageReportFilterComponent,
    UsageReportTableComponent,
    UsageReportChartComponent,
    TranslocoDecimalPipe,
    DefaultValuePipe,
    RouterLink,
  ],
})
export class UsageReportComponent implements OnInit {
  tableDraft: TableDraft = new TableDraftBuilder(TABLE_HEADER_AND_CELLS).disableRowLink().build();
  viewMode: UsageReportViewMode = UsageReportViewMode.CHART;
  total$: Subject<number> = new Subject<number>();
  isOrganizationAdmin$: Observable<boolean> = of(false);
  booking$!: Observable<BookingDataDetails>;
  filter?: UsageAggregationFilter;
  selectedServicePackage?: CartridgeServicePackage;

  protected readonly UsageReportViewModeMap = UsageReportViewModeMap;
  protected readonly UsageReportViewMode = UsageReportViewMode;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly bookingService: BookingService,
    private readonly cmsLanguageService: CMSLanguageService,
    private readonly snackbarService: SnackbarService,
    private readonly usageAggregationService: UsageAggregationService,
    private readonly authenticatedUserService: AuthenticatedUserService
  ) {}

  ngOnInit(): void {
    const bookingId = this.route.snapshot.paramMap.get('bookingId') as string;
    const organizationId = this.route.snapshot.paramMap.get('organizationId') as string;
    const cartridgeVariantId = this.route.snapshot.paramMap.get('cartridgeId') as string;

    this.isOrganizationAdmin$ = this.getOrganizationId().pipe(
      switchMap(organizationId =>
        this.authenticatedUserService
          .getUserRoles()
          .pipe(
            map(roles =>
              roles.some(
                ({ name, organizationId: orgId }) => name === StaticRole.ORGANIZATION_ADMIN && orgId === organizationId
              )
            )
          )
      )
    );

    this.booking$ = this.cmsLanguageService.activeCMSLanguage$.pipe(
      switchMap(() => {
        if (!bookingId) {
          return this.bookingService.getBookingByCartridgeVariantAndOrganization(cartridgeVariantId, organizationId);
        }

        return this.bookingService.getBookingById(bookingId);
      }),
      tap(({ cartridgeServicePackageData }) => {
        this.updateSelectedServicePackage(cartridgeServicePackageData, this.selectedServicePackage?.id);
        this.updateTable(this.filter);
      }),
      catchError(error => {
        this.snackbarService.showErrorSnackbarWithTransloco('cartridge.management.noManagementData', {
          traceId: error?.error?.traceId,
        });
        return EMPTY;
      })
    );
  }

  onUpdateFilter({ cartridgeServicePackageData }: BookingDataDetails, filter: UsageAggregationFilter): void {
    this.updateSelectedServicePackage(cartridgeServicePackageData, filter.servicePackageId);
    this.updateTable(filter);

    this.filter = { ...filter };
  }

  getAggregatedUsages = ({
    organizationId,
    cartridgeId,
  }: BookingDataDetails): ((
    filter: UsageAggregationFilter,
    index?: number,
    size?: number
  ) => Observable<UsageAggregationPage>) => {
    return (filter: UsageAggregationFilter, index = -1, size = -1) => {
      return this.usageAggregationService
        .getUsagesForServicePackageAggregated(organizationId, cartridgeId, filter, index, size)
        .pipe(
          tap(({ totalAggregation }) => {
            this.total$.next(totalAggregation);
          })
        );
    };
  };

  onViewModeChange(viewMode: UsageReportViewMode): void {
    this.viewMode = viewMode;

    this.filter = {
      ...this.filter,
    } as UsageAggregationFilter;
  }

  private updateSelectedServicePackage(
    cartridgeServicePackages?: CartridgeServicePackage[],
    selectedPackageId?: string
  ): void {
    this.selectedServicePackage = cartridgeServicePackages?.find(csp => csp.id === selectedPackageId);
  }

  private updateTable(filter?: UsageAggregationFilter): void {
    if (!filter) {
      return;
    }

    const { headerAndTableCells, ...draft } = this.tableDraft;
    this.tableDraft = {
      ...draft,
      headerAndTableCells: headerAndTableCells.map(cell => {
        if (cell.headerCellColumnId === 'id') {
          cell.tableCellDateFormat = UsageAggregationFrequencyDateFormatMap[filter.frequency];
        }
        if (cell.headerCellColumnId === 'totalUsages' && this.selectedServicePackage?.name) {
          cell.headerCellTitle = this.selectedServicePackage.name;
        }

        return cell;
      }),
    };
  }

  private getOrganizationId(): Observable<string> {
    const organizationId = this.route.snapshot.paramMap.get('organizationId');
    return organizationId ? of(organizationId) : this.authenticatedUserService.getOrganizationId();
  }
}
